import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  IconButton,
  ListItem,
  CircularProgress,
  Tooltip,
  Fade,
} from "@material-ui/core";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import AssignmentIcon from "@material-ui/icons/Assignment";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ContactAvatar from "../ContactAvatar";

const URGENCY_COLORS = {
  overdue: "#E53935",
  imminent: "#FB8C00",
  upcoming: "#FDD835",
};

const CARD_ICON_SIZE = 44;

const useStyles = makeStyles((theme) => ({
  item: {
    padding: theme.spacing(1.25, 2),
    cursor: "pointer",
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: "relative",
    borderLeft: "4px solid transparent",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(0, 0, 0, 0.03)",
    },
  },
  content: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    width: "100%",
  },
  avatar: {
    width: 44,
    height: 44,
    fontSize: "0.875rem",
    flexShrink: 0,
    border: theme.palette.type === "dark"
      ? "2px solid #2f2f2f"
      : "2px solid #fff",
    boxShadow: theme.palette.type === "dark"
      ? "0 4px 10px rgba(0,0,0,0.5)"
      : "0 4px 10px rgba(0,0,0,0.15)",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  cardAvatar: {
    width: 39,
    height: 39,
    flexShrink: 0,
    border: theme.palette.type === "dark"
      ? "2px solid #2f2f2f"
      : "2px solid #fff",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 4px 10px rgba(0,0,0,0.5)"
        : "0 4px 10px rgba(0,0,0,0.15)",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.03)",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  cardAvatarIcon: {
    width: `${CARD_ICON_SIZE}px !important`,
    height: `${CARD_ICON_SIZE}px !important`,
    fontSize: `${CARD_ICON_SIZE}px !important`,
  },
  fallbackAvatar: {
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    border: "2px solid transparent",
    boxSizing: "content-box",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  fallbackAvatarIcon: {
    fontSize: 36,
    color: "#14A45C",
  },


  info: {
    flex: 1,
    minWidth: 0,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  title: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  date: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    flexShrink: 0,
  },
  actionColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    alignSelf: "stretch",
    justifyContent: "flex-start",
    flexShrink: 0,
    minWidth: 64,
  },
  subtitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  bodyRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  bodyIcon: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  body: {
    fontSize: "0.8125rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  completeButton: {
    padding: 0,
    flexShrink: 0,
    marginTop: theme.spacing(1.25),
  },
  completeIcon: {
    fontSize: 22,
  },
}));

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return sameYear
    ? `${dd}/${mm} ${hh}:${mi}`
    : `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
};

const getUrgency = (dateStr) => {
  if (!dateStr) return { key: "none", label: "SEM DATA" };
  const target = new Date(dateStr).getTime();
  const now = Date.now();
  const diffMs = target - now;
  const diffH = diffMs / 3_600_000;

  if (diffMs < 0) return { key: "overdue", label: "ATRASADA" };
  if (diffH <= 3) {
    const hours = Math.max(1, Math.round(diffH));
    return { key: "imminent", label: `EM ${hours}H` };
  }

  const target0 = new Date(dateStr);
  target0.setHours(0, 0, 0, 0);
  const now0 = new Date();
  now0.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((target0 - now0) / 86_400_000);
  if (dayDiff <= 0) return { key: "upcoming", label: "HOJE" };
  if (dayDiff === 1) return { key: "upcoming", label: "AMANHÃ" };
  return { key: "upcoming", label: "EM BREVE" };
};

const getTypeIcon = (kind, type) => {
  if (kind === "schedule") return ChatBubbleOutlineIcon;
  const t = (type || "").toLowerCase();
  if (t === "call") return PhoneOutlinedIcon;
  if (t === "meeting") return EventOutlinedIcon;
  if (t === "message") return ChatBubbleOutlineIcon;
  if (t === "note") return DescriptionOutlinedIcon;
  return CheckCircleOutlineIcon;
};

const getScheduleDisabled = (schedule) => {
  if (!schedule) return false;
  if (schedule.sentAt) return true;
  if (schedule.status === "ENVIADA") return true;
  if (schedule.status === "AGENDADA") return true;
  return false;
};

const isMessageTaskScheduled = (task) =>
  task?.type === "message" && Boolean(task?.scheduleId);

const ActivityNotificationItem = ({ activity, onComplete, onNavigate }) => {
  const classes = useStyles();
  const [completing, setCompleting] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const { kind, schedule, task } = activity || {};

  const date = kind === "schedule" ? schedule?.sendAt : task?.dueDate;
  const urgency = useMemo(() => getUrgency(date), [date]);

  const contact =
    kind === "schedule" ? schedule?.contact : task?.contact || null;
  const kanbanCard = kind === "task" ? task?.kanbanCard : null;

  const title = useMemo(() => {
    if (kanbanCard) return kanbanCard.title || "-";
    if (contact?.name) return contact.name;
    return task?.text || "-";
  }, [contact, kanbanCard, task]);

  const subtitle = useMemo(() => {
    if (kanbanCard) {
      const boardName = kanbanCard?.kanbanBoard?.name;
      return boardName ? `CARD - ${String(boardName).toUpperCase()}` : "CARD";
    }
    if (contact?.number) return contact.number;
    if (kind === "task") return "ATIVIDADE";
    return "";
  }, [contact, kanbanCard, kind]);

  const body = useMemo(() => {
    if (kind === "schedule") {
      return schedule?.name || schedule?.body || "-";
    }
    return task?.text || task?.body || "-";
  }, [kind, schedule, task]);

  const cardIconColor = useMemo(() => {
    return kanbanCard?.tag?.color || "#14A45C";
  }, [kanbanCard]);

  const TypeIcon = getTypeIcon(kind, task?.type);

  const disabled = useMemo(() => {
    if (kind === "schedule") return getScheduleDisabled(schedule);
    if (isMessageTaskScheduled(task)) return false;
    return task?.status === "done";
  }, [kind, schedule, task]);

  const handleComplete = async (e) => {
    e.stopPropagation();
    if (completing || disabled) return;
    setCompleting(true);
    try {
      if (onComplete) {
        await onComplete(activity);
      }
      setLeaving(true);
    } catch {
      setCompleting(false);
    }
  };

  const handleClick = () => {
    if (onNavigate) {
      onNavigate(activity);
    }
  };

  const tooltipLabel = useMemo(() => {
    if (kind === "schedule") return "Enviar agora";
    if (isMessageTaskScheduled(task)) return "Enviar agora";
    return "Concluir";
  }, [kind, task]);

  return (
    <Fade in={!leaving} timeout={300} unmountOnExit>
      <ListItem
        className={classes.item}
        onClick={handleClick}
        style={{
          borderLeftColor: URGENCY_COLORS[urgency.key] || "transparent",
        }}
        disableGutters
        button
      >
        <Box className={classes.content}>
          {kanbanCard ? (
            <Box className={classes.cardAvatar}>
              <SpaceDashboardIcon
                className={classes.cardAvatarIcon}
                style={{ color: cardIconColor }}
              />
            </Box>
          ) : contact ? (
            <ContactAvatar
              src={contact.profilePicUrl}
              alt={contact.name || contact.number || "Contato"}
              className={classes.avatar}
            />
          ) : (
            <Box className={classes.fallbackAvatar}>
              <AssignmentIcon className={classes.fallbackAvatarIcon} />
            </Box>
          )}

          <Box className={classes.info}>
            <Box className={classes.headerRow}>
              <Typography className={classes.title}>{title}</Typography>
            </Box>

            {subtitle && (
              <Typography className={classes.subtitle}>{subtitle}</Typography>
            )}

            <Box className={classes.bodyRow}>
              <TypeIcon className={classes.bodyIcon} />
              <Typography className={classes.body}>{body}</Typography>
            </Box>
          </Box>

          <Box className={classes.actionColumn}>
            <Typography className={classes.date}>{formatDate(date)}</Typography>
            <Tooltip title={disabled ? "" : tooltipLabel}>
              <span>
                <IconButton
                  size="small"
                  className={classes.completeButton}
                  onClick={handleComplete}
                  disabled={disabled || completing}
                >
                  {completing ? (
                    <CircularProgress size={16} />
                  ) : disabled ? (
                    <CheckCircleIcon
                      className={classes.completeIcon}
                      style={{ color: "#4caf50", opacity: 0.5 }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon className={classes.completeIcon} style={{ color: "rgba(255, 255, 255, 0.6)" }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </ListItem>
    </Fade>
  );
};

export default ActivityNotificationItem;
